import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { formatNumber, getRemotedbUrl, i18nIntl, isNotEmpty } from '../../lib/ep.js';
import { useOwner } from '../context/context.js';

import Module from '../ui_items/Module.js';
import ButtonCollection from '../builder/ButtonCollection.js';
import { SecondaryButton } from '../builder/ActionButton.js';
import Message from '../builder/Message.js';

const Speedoemeter = ({ level, percent, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const angle_threshold = 10;	// minimum degrees up from horizontal level axis

	const min_angle = angle_threshold;
	const max_angle = 180 - angle_threshold;
	
	const pointer_angle = formatNumber((max_angle - min_angle) * percent - (90 - angle_threshold));

	return (
		<div class={`security_level is_${level}`} style={{ '--pointer-angle':`${pointer_angle}deg` }}>
			<div class="icon_container" style={{ width:'100%', height:'60px', overflow:'hidden' }}>
				<svg x="0" y="0" viewBox="0 0 130 80">
					<g class="meter">
						<path d="M100.56,16.75l-5.54,9.6c10.24,9.72,16.65,23.42,16.65,38.65H125C125,45.19,115.36,27.68,100.56,16.75z"/>

						<path d="M43.19,13.88c4.81-1.42,9.88-2.21,15.15-2.21c9.4,0,18.21,2.45,25.88,6.72l4.82-8.34C81.67,6.82,73.55,5,65,5 c-8.55,0-16.67,1.82-24.03,5.04L43.19,13.88z"/>

						<path d="M29.44,16.75C14.64,27.68,5,45.19,5,65c0-19.42,10.41-36.36,25.92-45.69L29.44,16.75z"/>
					</g>

					<g class="pointer">
						<path class="helper" d="M53.5,64.5C53.5,58.11,58.65,53,65,53s11.5,5.11,11.5,11.5S65,135,65,135S53.5,70.89,53.5,64.5z"/>

						<path class="p" d="M76.5,65.5C76.5,71.89,71.35,77,65,77s-11.5-5.11-11.5-11.5S65-5,65-5S76.5,59.11,76.5,65.5z"/>
					</g>

					<circle class="pin" cx="65" cy="65" r="8.5"/>
				</svg>
			</div>
			
			<div class="ribbon_label">
				{i18n(level[0].toUpperCase() + level.substring(1), 'uppercase')}
			</div>
		</div>
	)
};

const SecurityCenterMain = ({ ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const owner = useOwner();
	const promotion = owner?.promotion?.Promotion ?? false;

	if(promotion === false) return null;

	const debug = (window.server_type != '0') ? true : false;

	const items = [{
		order:1,
		title:"Email Verification at Login",
		description:i18n("A user could register with a non-existent email (i.e: 1212ddrr@gmail.com)."),
		action:i18n("Activate email verification to ensure the validity of the emails registered in the contest."),
		status:promotion?.login_token ?? false,
		href:getRemotedbUrl(`/promotions/builder/${promotion.id}`, 'login'),
		points_ok:2,
		points_ko:2,
		display:promotion?.login_net_mail ?? false,
	}, {
		order:2,
		title:"Limit the Customer Numbers at Login",
		description:i18n("The customer number validation on the login screen is not very secure."),
		action:i18n("We recommend you to activate the validation against a set of codes to restrict participation to a select group of users."),
		status:(['codeset', 'luhn', 'webservice'].includes(promotion?.code_login_validation ?? '')) ? true : false,
		href:getRemotedbUrl(`/promotions/builder/${promotion.id}`, 'login'),
		points_ok:2,
		points_ko:2,
		display:promotion?.login_net_code ?? false,
	}, {
		order:3,
		title:"Registration Limited to a Group of Non-Generic Email Addresses",
		status:true,
		points_ok:4,
		points_ko:0,
		display:((promotion?.whitelist_domains ?? []).length > 0) ? true : false,
	}, {
		order:4,
		title:"ReCaptcha-Based Anti-Bot System",
		description:i18n("A user who uses programs to automate the registration of multiple participants could register into the promotion."),
		action:props.intl.formatMessage({ id:"We recommend you to activate it and set the sensitivity level to {level}." }, { level:`"${i18n("***Medium")}"` }),
		status:promotion?.recaptcha_validation ?? false,
		href:getRemotedbUrl(`/promotions/builder/${promotion.id}`, 'register/security'),
		points_ok:2,
		points_ko:0,
	}, {
		order:5,
		title:"Connection Country Restriction",
		description:i18n("Currently, your promotion allows connections from any country."),
		action:i18n("We recommend you to apply country restrictions if your promotion is limited to one or several specific countries."),
		status:(isNotEmpty(promotion, 'countries')) ? (promotion.countries.split(",").length > 0 ? true : false) : false,
		href:getRemotedbUrl(`/promotions/builder/${promotion.id}`, 'restrictions/countries'),
		points_ok:1,
		points_ko:0,
	}, {
		order:6,
		title:"IP Address Frequency Control",
		description:i18n("Currently, the registration frequency control by IP is deactivated. It is advisable to activate it for any online promotion."),
		action:i18n("Deactivate it only if your promotion runs on a single device, such as a tablet at a fair or congress."),
		status:(parseInt(promotion?.enter_ip_delay ?? '0') > 0) ? true : false,
		href:getRemotedbUrl(`/promotions/builder/${promotion.id}`, 'register/security'),
		points_ok:1,
		points_ko:0,
	}, {
		order:7,
		title:"Registration Limit per Browser/IP",
		description:i18n("Currently, there is no limit on registrations per device defined by the combination of its IP and browser version. It is advisable to set a limit for any online promotion."),
		action:i18n("Deactivate it only if your promotion runs on a single device, such as a tablet at a fair."),
		status:(parseInt(promotion?.max_entries_hash ?? '0') > 0) ? true : false,
		href:getRemotedbUrl(`/promotions/builder/${promotion.id}`, 'register/security'),
		points_ok:1,
		points_ko:0,
	}];

	const checkIfDone = (item) => {
		return item?.status ?? false;
	};

	const checkIfDisplay = (item) => {
		return item?.display ?? true;
	};

	const done_filtered_items = items.filter((item) => {
		if(!checkIfDisplay(item)) return false;
		if(!checkIfDone(item)) return false;
		return true;
	});

	const pending_filtered_items = items.filter((item) => {
		if(!checkIfDisplay(item)) return false;
		if(checkIfDone(item)) return false;
		return true;
	});

	const filtered_items = [...pending_filtered_items, ...done_filtered_items];

	const calculateMaxPoints = () => {
		let ok_points = 0;
		let ko_points = 0;
		filtered_items.map((item) => {
			ok_points += item?.points_ok ?? 0;
			ko_points += item?.points_ko ?? 0;
		});

		return { ok_points, ko_points, max_points:ok_points + ko_points };
	};

	const { ok_points, ko_points, max_points } = calculateMaxPoints();

	const calculatePoints = () => {
		let points = ko_points;	// we start with all the points that can be deducted (so at minimum we can have 0 points)
		filtered_items.map((item) => {
			const is_done = checkIfDone(item);

			if(is_done) {
				points += item?.points_ok ?? 0;
			} else {
				points -= item?.points_ko ?? 0;
			}
		});

		return points - ko_points;
	};

	const calculateSecurityLevel = () => {
		const points = calculatePoints();

		const low_max = 3;
		const high_min = 5;

		let level = 'medium';
		if(points > (high_min)) level = 'high';
		if(points < (low_max)) level = 'low';

		let multiplier = 1;
		switch(level) {
			case 'high':
				multiplier = 1.3;
				break;

			case 'medium':
				multiplier = 0.9;
				break;

			case 'low':
			default:
				multiplier = 0.5;
				break;
		}

		const real_percent = Math.max(0, Math.min(1, points / max_points));
		const percent = Math.max(0, Math.min(1, multiplier * points / max_points));

		return { level, points, percent, real_percent, low_max, high_min };
	};

	const { level, points, percent, real_percent, low_max, high_min } = calculateSecurityLevel();

	const login_type = promotion?.login_type ?? '1';	// by default: identified_mode

	const is_anonymous = (login_type == '3') ? true : false;
	const is_autologin = (login_type == '2') ? true : false;
	const is_openid_connect = (login_type == '5') ? true : false;
	const is_realmadrid_login = promotion?.login_net_madrid ?? false;

	const is_external_login = (is_autologin || is_openid_connect || is_realmadrid_login)  ? true : false;
	const analyze_security_level = (!is_external_login && !is_anonymous) ? true : false;

	if(is_external_login) {
		return (
			<>
				<Module
					title="Security level"
					subtitle="Security level of your identification and registration system for the promotion. The main abuses by participating users in contests occur during registration. Ensure that the registration for your promotion always maintains a high level of security."
					force_show={true}
				/>

				<Message _class={{ other:'in_top in_bottom' }}>
					<p>{i18n("The Security Level indicator is disabled when the identification and registration system is external: Autologin, OpenID Connect or SSO integrations.")}</p>
				</Message>
			</>
		)
	}

	if(is_anonymous) {
		return (
			<>
				<Module
					title="Security level"
					subtitle="Security level of your identification and registration system for the promotion. The main abuses by participating users in contests occur during registration. Ensure that the registration for your promotion always maintains a high level of security."
					force_show={true}
				/>

				<Message _class={{ other:'in_top in_bottom' }}>
					<p>{i18n("The Security Level indicator is disabled when the identification and registration system is unpublished: Anonymous promotions.")}</p>
				</Message>
			</>
		)
	}

	return (
		<>
			<Module
				title="Security level"
			>
				<div style={{ display:'grid', gridTemplateColumns:'1fr 100px', gap:'1em' }}>
					<div>
						<p>{i18n("Security level of your identification and registration system for the promotion. The main abuses by participating users in contests occur during registration. Ensure that the registration for your promotion always maintains a high level of security.")}</p>

						{(debug && analyze_security_level) && (
							<p class='not_in_top' style={{ backgroundColor:'rgba(255, 0, 0, 0.1)', display:'inline-block' }}>
								<strong style={{ backgroundColor:'rgba(255, 0, 0, 0.1)', padding:'0.3em 0.7em', borderBottom:'1px solid', display:'block' }}>[{points} points (max: {ok_points} / ini: {ko_points}) ({formatNumber(100 * real_percent)}%)]</strong><br />
								
								<span style={{ display:'grid', padding:'0 0.7em 0.3em', gridTemplateColumns:'8ch 1fr' }}>
									<strong>LOW</strong><span>{0} ≤ points &lt; {low_max}</span>
									<strong>MEDIUM</strong><span>{low_max} ≤ points ≤ {high_min}</span>
									<strong>HIGH</strong><span>{high_min} &lt; points ≤ {max_points}</span>
								</span>
							</p>
						)}
					</div>
					
					<Speedoemeter
						level={level}
						points={points}
						percent={percent}
						intl={props.intl}
					/>
				</div>
			</Module>

			{analyze_security_level && filtered_items.map((item, i) => {
				const is_done = checkIfDone(item);
				const has_action = (!is_done && isNotEmpty(item, 'href')) ? true : false;

				return (
					<Module
						key={`item_${i}`}
						force_show={true}
						title={`${i18n(item.title)}${(debug) ? ` (OK: ${item?.points_ok ?? 0} | KO: ${-1 * (item?.points_ko ?? 0)})` : ''}`}
						icon={(is_done) ? 'ep-check-list-item greenIcon' : 'ep-delete-circle redIcon'}
						autotranslate={false}
						_class={{ padding:'normal', other:`gui_not_padded_in_top_small${(!has_action) ? ' gui_not_padded_in_bottom_small' : ''}` }}
						header_class={(is_done) ? { other:'not_bordered' } : null}
					>
						{(!is_done) && (
							<p>
								{(isNotEmpty(item, 'description')) ? item.description : ''}

								{(isNotEmpty(item, 'action')) && (
									<strong> {item.action}</strong>
								)}
							</p>
						)}

						{(has_action) && (
							<ButtonCollection _class={{ other:'gui_flex_col gui_flex_align_top' }}>
								<SecondaryButton
									_href={item.href}
									target={item?.target ?? '_blank'}
								>Review</SecondaryButton>
							</ButtonCollection>
						)}
					</Module>
				)
			})}
		</>
	);
}

export default injectIntl(SecurityCenterMain);